import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Hammer, ArrowLeft, TrendingUp, Clock, Users, Building, CheckCircle } from 'lucide-react';

const ConstructionCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/industries" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Industries
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-xl flex items-center justify-center">
                <Hammer className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Building & Construction
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  Horizon Lumber & Building
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              How a lumber yard grew contractor accounts by 22% with automated loyalty pricing
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1634672652995-ee7525bce595?w=1200"
              alt="Horizon Lumber & Building Yard"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">165</div>
              <div className="text-[#94A3B8]">Contractor Accounts</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Building className="w-8 h-8 text-green-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">3</div>
              <div className="text-[#94A3B8]">Yard Locations</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-yellow-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">3 generations</div>
              <div className="text-[#94A3B8]">Family-Owned Business</div>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                Horizon Lumber & Building has been a fixture in their community for three generations. But their contractor pricing system was stuck in the past—literally written on index cards in the sales manager's desk drawer.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                Every contractor had a different deal, negotiated over handshakes and years of relationship-building. New salespeople had no idea what discount to offer. Longtime contractors complained when they didn't get "their" price. And material cost volatility meant the business was often eating margin increases instead of passing them through.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed">
                The third-generation owner knew they needed to systematize their pricing, but worried about alienating the contractors who kept the business running.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-6">
                PriceStrike helped Horizon Lumber create a transparent, automated contractor pricing system:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Tiered Contractor Program:</span>
                    <span className="text-[#94A3B8]"> Created Bronze, Silver, Gold, and Platinum tiers based on annual spend, with clear benefits at each level.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Automatic Tier Progression:</span>
                    <span className="text-[#94A3B8]"> Contractors automatically move up tiers as their spending increases, creating natural incentives to consolidate purchases.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Cost Pass-Through Rules:</span>
                    <span className="text-[#94A3B8]"> When lumber prices spike, automatic adjustments flow through to contractor pricing with advance notice.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Quick Quote Generation:</span>
                    <span className="text-[#94A3B8]"> Any salesperson can generate accurate job quotes in seconds with contractor-specific pricing applied automatically.</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-[#111827] border border-green-500/30 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">22%</div>
                  <div className="text-[#94A3B8]">Increase in contractor accounts</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">35%</div>
                  <div className="text-[#94A3B8]">Higher average order value</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">90%</div>
                  <div className="text-[#94A3B8]">Reduction in pricing disputes</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">2 days</div>
                  <div className="text-[#94A3B8]">New salesperson ramp-up time</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#111827] border border-[#2563EB]/30 rounded-2xl p-8 mb-12">
              <blockquote className="text-xl text-[#F8FAFC] italic mb-4">
                "Contractors love knowing exactly what discount they're getting. No more negotiating every order. And when lumber prices went crazy last year, we could pass through increases fairly instead of eating the cost."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">JH</span>
                </div>
                <div>
                  <div className="text-[#F8FAFC] font-semibold">Jake Horizon III</div>
                  <div className="text-[#94A3B8]">Owner, Horizon Lumber & Building</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/coming-soon">
              <button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Start Your Free Trial
              </button>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ConstructionCaseStudy;
